import { FC, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import dayjs, { Dayjs } from "dayjs";
import { GrantTraderAccessRequest, UserDto } from "../../api/Api";
import { UserPicker } from "../userPicker";

type Props = {
  open: boolean;
  entityId: string;
  onClose: () => void;
  onSubmit: (body: GrantTraderAccessRequest) => void;
  isSubmitting: boolean;
};

type ExpiryPreset = "30" | "90" | "180" | "none";

const presets: { value: ExpiryPreset; label: string }[] = [
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "180", label: "180 days" },
  { value: "none", label: "No expiry" }
];

export const GrantDialog: FC<Props> = ({
  open,
  entityId,
  onClose,
  onSubmit,
  isSubmitting
}) => {
  const [selected, setSelected] = useState<UserDto | null>(null);
  const [preset, setPreset] = useState<ExpiryPreset>("90");
  const [customExpiry, setCustomExpiry] = useState<Dayjs | null>(null);

  const effectiveExpiry = useMemo<Dayjs | null>(() => {
    if (customExpiry) return customExpiry;
    if (preset === "none") return null;
    return dayjs().add(Number(preset), "day");
  }, [preset, customExpiry]);

  const handlePreset = (value: ExpiryPreset) => {
    setPreset(value);
    setCustomExpiry(null);
  };

  const handleCustomExpiry = (value: Dayjs | null) => {
    setCustomExpiry(value);
  };

  const handleSubmit = () => {
    if (!selected?.id || !selected.name) return;
    onSubmit({
      entityId,
      userObjectId: selected.id,
      displayName: selected.name,
      email: selected.email,
      department: selected.department,
      expiresAt: effectiveExpiry ? effectiveExpiry.toISOString() : null
    });
  };

  const handleClose = () => {
    setSelected(null);
    setPreset("90");
    setCustomExpiry(null);
    onClose();
  };

  const canSubmit = !!selected?.id && !!selected?.name && !isSubmitting;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Grant trader access</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <UserPicker value={selected} onChange={setSelected} />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Expires
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {presets.map((p) => (
                <Chip
                  key={p.value}
                  label={p.label}
                  color={
                    preset === p.value && !customExpiry ? "primary" : "default"
                  }
                  onClick={() => handlePreset(p.value)}
                />
              ))}
            </Stack>
            <Box sx={{ mt: 2 }}>
              <DatePicker
                label="Custom date"
                value={customExpiry}
                onChange={handleCustomExpiry}
                minDate={dayjs().add(1, "day")}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              {effectiveExpiry
                ? `Access will expire on ${effectiveExpiry.format("LL")}.`
                : "Access will not expire automatically."}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
        >
          {isSubmitting ? "Granting..." : "Grant access"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
